<template>
  <View gap="md" direction="column">
    <Tabs :model-value="activeTab" :tabs="engagementTabs" @update:model-value="activeTab = $event as Tab" />

    <TabTransition :active-key="activeTab">
      <PropertyDashboardShowingRequests v-if="activeTab === Tab.SHOWING_REQUESTS" :hide-header="true" />

      <PropertyDashboardEscalations v-else-if="activeTab === Tab.ESCALATIONS" :hide-header="true" />

      <PropertyDashboardWantsToApply v-else-if="activeTab === Tab.WANTS_TO_APPLY" />

      <View v-else direction="column" gap="md">
        <View v-if="isPending" justify="center" padding-block="lg">
          <LoadingIndicator />
        </View>

        <EmptyState
          v-else-if="bundles.length === 0"
          icon="file-04"
          color="blue"
          title="No applications yet"
          description="Applications sent to renters for this property will appear here."
        />

        <ScrollableHorizontallyContainer v-else>
          <View class="card-items" gap="md" padding-bottom="md">
            <ApplicationBundleCard
              v-for="bundle in bundles"
              :key="bundle.id"
              :application-bundle="bundle"
              class="card-item"
            />
          </View>
        </ScrollableHorizontallyContainer>
      </View>
    </TabTransition>
  </View>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import {
  EmptyState,
  LoadingIndicator,
  ScrollableHorizontallyContainer,
  Tabs,
  TabTransition,
  View,
  type TabItem,
} from '@tallo/design-system';
import { ApplicationBundleCard, usePropertyApplicationBundlesQuery } from '@tallo/investor/applications';
import { usePropertyIntelligentEscalationsQuery } from '@tallo/investor/intelligent-escalations';
import { usePropertyWantsToApplyQuery } from '@tallo/investor/property';
import { useShowingsQuery } from '@tallo/investor/showing';

import PropertyDashboardEscalations from './property-dashboard-escalations.vue';
import PropertyDashboardShowingRequests from './property-dashboard-showing-requests.vue';
import PropertyDashboardWantsToApply from './property-dashboard-wants-to-apply.vue';

enum Tab {
  SHOWING_REQUESTS = 'showing-requests',
  ESCALATIONS = 'escalations',
  WANTS_TO_APPLY = 'wants-to-apply',
  APPLICATIONS = 'applications',
}

const route = useRoute();
const propertyId = computed(() => route.params.propertyId as string);

// Every owner escalation notification links here with `?questionId=`. The escalations pane opens it,
// but only once its tab is showing - land there, or the link shows the showing requests instead.
const activeTab = ref(route.query.questionId ? Tab.ESCALATIONS : Tab.SHOWING_REQUESTS);

watch(
  () => route.query.questionId,
  (questionId) => {
    if (questionId) {
      activeTab.value = Tab.ESCALATIONS;
    }
  },
);

const { data, isPending } = usePropertyApplicationBundlesQuery();
const bundles = computed(() => data.value ?? []);

// Same query keys as the tab panes, so these share the panes' cache entries instead of refetching.
const { data: pendingShowingsData } = useShowingsQuery({ propertyId, onlyWithPendingShowingReqests: true });
const { data: escalationsData } = usePropertyIntelligentEscalationsQuery();
const { data: wantsToApplyData } = usePropertyWantsToApplyQuery();

// The product tour selects the first two tabs by id while it walks the owner through the panes.
const engagementTabs = computed<TabItem[]>(() => [
  {
    key: Tab.SHOWING_REQUESTS,
    id: 'pd-tab-showing-requests',
    label: 'Showing requests',
    dot: (pendingShowingsData.value?.length ?? 0) > 0,
  },
  {
    key: Tab.ESCALATIONS,
    id: 'pd-tab-escalations',
    label: 'Intelligent Escalations',
    dot: (escalationsData.value?.length ?? 0) > 0,
  },
  {
    key: Tab.WANTS_TO_APPLY,
    id: 'pd-tab-wants-to-apply',
    label: 'Wants to apply',
    dot: (wantsToApplyData.value?.length ?? 0) > 0,
  },
  { key: Tab.APPLICATIONS, label: 'Application', dot: bundles.value.length > 0 },
]);
</script>

<style scoped lang="scss">
@import 'styles/mixins/layout.mixins';

// The applications pane's scroll container sits right under the tabs, and its negative top bleed
// margin (card-shadow clearance) extends up over them and intercepts their clicks. Drop the TOP
// bleed only, same as the escalations pane does for its own container.
:deep(.container-outer) {
  margin-top: 0;
}

:deep(.container-inner) {
  padding-top: 0;
}

.card-items {
  .card-item {
    width: 28rem;
    flex-shrink: 0;
  }

  @include mobile {
    flex-direction: column;

    .card-item {
      width: 100%;
    }
  }
}
</style>
